import platformClient from 'purecloud-platform-client-v2';
import { wizardConfig } from '../config/wizard-config';
import {
  clearGenesysSession,
  currentGenesysSession,
  readStoredGenesysSession,
  setGenesysSession,
  type GenesysSession,
} from './token-store';
import { isGenesysAuthFailure, reportSessionExpired } from './session';
import { runPopupSignIn } from './auth-popup-guard';

const client = platformClient.ApiClient.instance;
const usersApi = new platformClient.UsersApi();
const integrationsApi = new platformClient.IntegrationsApi();
const authorizationApi = new platformClient.AuthorizationApi();
const oauthApi = new platformClient.OAuthApi();
const organizationApi = new platformClient.OrganizationApi();
const tokensApi = new platformClient.TokensApi();

/**
 * Every SDK request goes through `callApi`. A 401 while a signed-in token is
 * current means the session expired, whichever screen made the call.
 */
const sdkCallApi = client.callApi.bind(client);
client.callApi = (...args: unknown[]) => sdkCallApi(...args).catch((err: unknown) => {
  if (currentGenesysSession() && isGenesysAuthFailure(err)) reportSessionExpired();
  throw err;
});

export function getClient() {
  return client;
}

export function getAccessToken(): string {
  return client.authentications['PureCloud OAuth']?.accessToken || '';
}

/**
 * The SDK never persists the token (localStorage would share it with every tab
 * and outlive the browser session). token-store keeps it in this tab's sessionStorage.
 */
function keepTokenOutOfLocalStorage(): void {
  client.setPersistSettings(false, wizardConfig.premiumAppIntegrationTypeId);
}

/** Forgets the token before a new sign-in, so the SDK does not reuse a dead one. */
export function clearGenesysToken(): void {
  const oauth = client.authentications['PureCloud OAuth'];
  if (oauth) oauth.accessToken = '';
  if (client.authData) {
    delete client.authData.accessToken;
    delete client.authData.tokenExpiryTime;
  }
  clearGenesysSession();
}

/** Asks Genesys whether the current token still works. Only a 401 counts as expired. */
export async function checkGenesysTokenAlive(): Promise<'ok' | 'expired' | 'unknown'> {
  try {
    await tokensApi.getTokensMe();
    return 'ok';
  } catch (err) {
    return (err as { status?: number } | null)?.status === 401 ? 'expired' : 'unknown';
  }
}

/** True when the wizard runs inside the Genesys Apps iframe (not a top-level tab). */
export function isEmbeddedInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * When Genesys redirects the auth popup back to this same `index.html`, the
 * Platform SDK in the opener is waiting for a `gc_auth_popup` postMessage.
 * Relay and skip mounting the wizard so the popup does not start a second
 * PKCE grant (its sessionStorage has no code_verifier).
 */
export function relayAuthPopupIfOpener(): boolean {
  if (!window.opener || window.opener.closed) return false;
  const params = new URLSearchParams(window.location.search);
  if (!params.has('code') && !params.has('error')) return false;
  try {
    window.opener.postMessage(
      {
        name: 'gc_auth_popup',
        type: 'message',
        search: window.location.search,
        hash: window.location.hash || '',
      },
      window.location.origin,
    );
    return true;
  } catch (err) {
    console.warn('[AUTH] Failed to relay Genesys login popup callback:', err);
    return false;
  }
}

/**
 * Try to reuse this tab's Genesys access token (no popup, no redirect).
 * Used on Apps-iframe load so already-installed orgs go straight to Landing
 * when the token is still valid. A token near its expiry is not reused.
 * Returns false when interactive sign-in is required.
 */
export async function tryRestoreGenesysSession(gcEnvironment: string): Promise<boolean> {
  client.setEnvironment(gcEnvironment);
  keepTokenOutOfLocalStorage();

  const stored = readStoredGenesysSession();
  if (!stored) {
    console.info('[AUTH] No usable Genesys token in this tab; interactive sign-in required');
    clearGenesysToken();
    return false;
  }
  try {
    const oauth = client.authentications['PureCloud OAuth'];
    if (oauth) oauth.accessToken = stored.accessToken;
    await usersApi.getUsersMe({ expand: ['organization'] });
    setGenesysSession(stored);
    console.info('[AUTH] Restored Genesys session from this tab (no popup)');
    return true;
  } catch (err) {
    console.info('[AUTH] Stored Genesys token invalid; interactive sign-in required', err);
    clearGenesysToken();
    return false;
  }
}

/**
 * Records the token the SDK now holds: the stored session when the SDK kept
 * that token, else the expiry Genesys gave for the new grant.
 */
function recordSignedInToken(stored: GenesysSession | null): void {
  const accessToken = getAccessToken();
  if (!accessToken) return;
  if (currentGenesysSession()?.accessToken === accessToken) return;
  if (stored?.accessToken === accessToken) {
    setGenesysSession(stored);
    return;
  }
  const expiry = typeof client.authData?.tokenExpiryTime === 'number' ? client.authData.tokenExpiryTime : null;
  setGenesysSession({ accessToken, expiresAt: expiry, issuedAt: Date.now() });
}

let signingIn: Promise<Record<string, string>> | null = null;

/**
 * Authenticate admin via Authorization Code Grant with PKCE.
 *
 * Matches the Genesys Premium App Wizard reference implementation
 * (https://github.com/MyPureCloud/purecloud-premium-app — converted from
 * Implicit Grant to PKCE on 2026-04-13). The legacy Implicit Grant flow is
 * deprecated by OAuth 2.1 and breaks cross-region OAuth client lookup.
 *
 * When the wizard is embedded in a Genesys Apps iframe, login uses the
 * Platform SDK popup helper (`authPopupConfiguration.usePopup`) so the
 * Genesys login page is not rendered inside the iframe (deprecated
 * 2026-08-31 for new integrations). Standalone / top-level loads keep the
 * existing same-window redirect. Same registered redirect URI either way
 * (`${wizardUriBase}index.html`).
 *
 * The launch params are round-tripped through the OAuth `state` blob so that
 * `App.tsx` can restore them on the redirect-back leg (when Genesys drops the
 * original query string).
 */
export function authenticateGenesysCloud(
  gcEnvironment: string,
  appParams: Record<string, string>,
): Promise<Record<string, string>> {
  // One sign-in at a time: a second setup run (React StrictMode in dev) waits for
  // the first instead of testing a stale token and starting another login.
  signingIn ??= signInWithGenesys(gcEnvironment, appParams).finally(() => {
    signingIn = null;
  });
  return signingIn;
}

async function signInWithGenesys(
  gcEnvironment: string,
  appParams: Record<string, string>,
): Promise<Record<string, string>> {
  client.setEnvironment(gcEnvironment);
  keepTokenOutOfLocalStorage();
  // A reload in this tab reuses its token; the SDK checks it before any new login.
  // It becomes the session only once the SDK accepts it, so a dead one is not
  // reported as an expired session while the page is still starting.
  const oauth = client.authentications['PureCloud OAuth'];
  const stored = getAccessToken() ? null : readStoredGenesysSession();
  if (oauth && stored) oauth.accessToken = stored.accessToken;

  const usePopup = isEmbeddedInIframe();
  if (usePopup) {
    console.info('[AUTH] Starting Genesys PKCE via popup (embedded Apps iframe)');
    client.onAuthPopupStatus = (status, msg) => {
      console.info('[AUTH] Genesys login popup:', status, msg || '');
      if (status === 'ERROR' || status === 'AUTH_ERROR' || status === 'TIMEOUT' || status === 'ABORTED') {
        console.warn('[AUTH] Genesys login popup failed:', status, msg);
      }
    };
  } else {
    console.info('[AUTH] Starting Genesys PKCE via same-window redirect (top-level)');
  }

  const login = () => client.loginPKCEGrant(
    wizardConfig.clientID,
    `${wizardConfig.wizardUriBase}index.html`,
    {
      state: JSON.stringify(appParams),
      ...(usePopup
        // No delay: the SDK's delayed close reads the popup when it fires, so it
        // would close a new sign-in popup started within that delay.
        ? { authPopupConfiguration: { usePopup: true, popupTimeout: 120000, autoClosePopupDelay: 0 } }
        : {}),
    },
  );
  const authData = usePopup ? await runPopupSignIn(login) : await login();
  recordSignedInToken(stored);

  if (!authData.state) return {};
  try {
    return JSON.parse(authData.state);
  } catch {
    return {};
  }
}

/**
 * Get the current user including org and authorization details.
 */
export async function getCurrentUser() {
  return usersApi.getUsersMe({ expand: ['organization', 'authorization'] });
}

/**
 * Get the current organization details including shortName.
 */
export async function getOrganizationMe() {
  return organizationApi.getOrganizationsMe();
}

/**
 * Result of the product-availability check. `available` answers "does this org have the
 * MediaBriz integration type?"; `permissionDenied` distinguishes the case where we could
 * NOT answer that because the signed-in user lacks the Genesys "Integrations > Integration
 * > View" permission (`integrations:integration:view`) that GET /api/v2/integrations/types
 * requires — an HTTP 403. That is NOT the same as an inactive subscription (a successful
 * call that simply didn't find the type), so callers can show an accurate message and let
 * already-installed orgs bypass the check for non-admin viewers.
 */
export interface ProductAvailabilityResult {
  available: boolean;
  permissionDenied: boolean;
}

/**
 * Validate that the premium app integration type exists (product purchased).
 */
export async function validateProductAvailability(): Promise<ProductAvailabilityResult> {
  const getPage = async (pageNum: number): Promise<boolean> => {
    const data = await integrationsApi.getIntegrationsTypes({
      pageSize: 100,
      pageNumber: pageNum,
    });

    const found = data.entities?.find(
      (t: { id: string }) => t.id === wizardConfig.premiumAppIntegrationTypeId,
    );

    if (found) return true;
    if (data.nextUri) return getPage(pageNum + 1);
    return false;
  };

  try {
    return { available: await getPage(1), permissionDenied: false };
  } catch (e) {
    // A 403 means the signed-in user lacks integrations:integration:view — NOT that the
    // org hasn't purchased MediaBriz. Surface it distinctly (see ProductAvailabilityResult).
    const status = (e as { status?: number } | null)?.status;
    console.error('Product availability check failed:', e);
    return { available: false, permissionDenied: status === 403 };
  }
}

export interface GenesysDivision {
  id: string;
  name: string;
}

/**
 * List the org's authorization divisions (for the Conversation Actions
 * division allowlist picker). Throws when the signed-in admin lacks the
 * required permission — callers fall back to manual division-ID input.
 */
export async function listDivisions(): Promise<GenesysDivision[]> {
  const divisions: GenesysDivision[] = [];
  let pageNumber = 1;
  // Bounded paging: 50 pages x 100 = 5,000 divisions, far beyond any real org.
  for (;;) {
    const page = await authorizationApi.getAuthorizationDivisions({
      pageSize: 100,
      pageNumber,
    });
    for (const d of page.entities || []) {
      if (d.id) divisions.push({ id: d.id, name: d.name || d.id });
    }
    if (!page.nextUri || pageNumber >= 50) break;
    pageNumber += 1;
  }
  return divisions;
}

/**
 * Create the MediaBriz Code Authorization (PKCE) OAuth Client.
 */
export async function createOAuthClient(): Promise<{
  id: string;
  name: string;
  secret: string;
}> {
  const oauthConfig = wizardConfig.provisioningInfo.oauthClient;
  const result = await oauthApi.postOauthClients({
    name: `${wizardConfig.prefix}${oauthConfig.name}`,
    description: oauthConfig.description,
    authorizedGrantType: oauthConfig.authorizedGrantType,
    accessTokenValiditySeconds: oauthConfig.accessTokenValiditySeconds,
    registeredRedirectUri: [...oauthConfig.registeredRedirectUri],
    scope: [...oauthConfig.scope],
  });
  return { id: result.id, name: result.name, secret: result.secret };
}

/**
 * Delete a role by ID.
 */
export async function deleteRole(roleId: string): Promise<void> {
  await authorizationApi.deleteAuthorizationRole(roleId);
}

/**
 * Delete an OAuth client by ID.
 * Genesys requires clients to be deactivated before deletion.
 */
export async function deleteOAuthClient(clientId: string): Promise<void> {
  const oauthClient = await oauthApi.getOauthClient(clientId);
  if (oauthClient.state !== 'inactive') {
    await oauthApi.putOauthClient(clientId, {
      name: oauthClient.name,
      description: oauthClient.description,
      authorizedGrantType: oauthClient.authorizedGrantType,
      accessTokenValiditySeconds: oauthClient.accessTokenValiditySeconds,
      registeredRedirectUri: oauthClient.registeredRedirectUri,
      scope: oauthClient.scope,
      state: 'inactive',
    });
  }
  await oauthApi.deleteOauthClient(clientId);
}

/**
 * Find the legacy role older wizard versions created ("Mediabriz App").
 * Exact name only: an org's own roles that merely start with the prefix are never matched.
 */
export async function findExistingRoles(): Promise<Array<{ id: string; name: string }>> {
  const legacyRoleName = `${wizardConfig.prefix}${wizardConfig.provisioningInfo.role.name}`;
  const roles = await authorizationApi.getAuthorizationRoles({
    name: `${wizardConfig.prefix}*`,
    pageSize: 100,
  });
  return (roles.entities || [])
    .filter((r: { name: string }) => r.name === legacyRoleName)
    .map((r: { id: string; name: string }) => ({ id: r.id, name: r.name }));
}

/**
 * Find existing wizard-created OAuth clients (CODE grant type with matching prefix).
 */
export async function findExistingOAuthClients(): Promise<Array<{ id: string; name: string }>> {
  const clients = await oauthApi.getOauthClients();
  return (clients.entities || [])
    .filter((c: { name: string; authorizedGrantType?: string }) =>
      c.name.startsWith(wizardConfig.prefix) && c.authorizedGrantType === 'CODE',
    )
    .map((c: { id: string; name: string }) => ({ id: c.id, name: c.name }));
}
